import { Link, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { PageBody, TabList, tabVariants } from '@wundo/ui';
import { PageTitle } from '@/components/PageTitle.tsx';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { canLinkSlack } from '@/lib/slackInstallation.ts';
import { availableInDemo } from '@/lib/availableInDemo.ts';
import { useDemoHoldBack } from '@/lib/useDemoHoldBack.ts';
import { m } from '@/paraglide/messages.js';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsLayout,
});

/*
 * Tabs are routes rather than local state so a settings section can be linked
 * to, and so a refresh lands you back where you were.
 *
 * Active state is derived from the pathname rather than from Link's
 * activeProps: the variants produce complete class strings, and letting the
 * router merge two of them would leave the active and inactive borders both
 * applied, with stylesheet order deciding the winner.
 */
const SETTINGS_TABS = [
  { to: '/settings/profile', label: m.settings_tab_profile },
  { to: '/settings/agenda', label: m.settings_tab_agenda },
  { to: '/settings/meetings', label: m.settings_tab_meetings },
  { to: '/settings/notifications', label: m.settings_tab_notifications },
  { to: '/settings/templates', label: m.settings_tab_templates },
  { to: '/settings/connections', label: m.settings_tab_connections },
] as const;

export function SettingsLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  /* Koppelingen holds the calendars and, for an admin, Slack: without either it is empty. */
  const tabs = SETTINGS_TABS.filter(
    (tab) =>
      tab.to !== '/settings/connections' ||
      user?.calendar_enabled !== false ||
      canLinkSlack(user),
  );
  const holdBack = useDemoHoldBack();

  return (
    <>
      <PageTitle>{m.settings_title()}</PageTitle>
      <PageBody>
        <TabList>
          {tabs.map(({ to, label }) => {
            const active = pathname === to;
            return (
              <Link
                key={to}
                to={to}
                role='tab'
                aria-selected={active}
                className={tabVariants({ active })}
                {...holdBack(!availableInDemo(to))}
              >
                {label()}
              </Link>
            );
          })}
        </TabList>
        {/* Settings rows read better in a narrower column than the rest of the app. */}
        <div className='mt-6 max-w-190'>
          <Outlet />
        </div>
      </PageBody>
    </>
  );
}
